import React, { useState, useEffect } from 'react';
import { Search, Users, X, Crown, Star } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { customerApi, vpnApi } from '../api';
import { isVipActive, ENABLE_VPN } from '../components/customers/constants';

import CustomerTable       from '../components/customers/CustomerTable';
import VipDialog           from '../components/customers/VipDialog';
import AgentDialog         from '../components/customers/AgentDialog';
import BalanceAdjustDialog from '../components/customers/BalanceAdjustDialog';
import VpnAssignDialog     from '../components/customers/VpnAssignDialog';
import InfoTab             from '../components/customers/tabs/InfoTab';
import BalanceTab          from '../components/customers/tabs/BalanceTab';
import CommissionTab       from '../components/customers/tabs/CommissionTab';

const CustomersPage = () => {
  const { currentUser } = useApp();
  const isAdmin = currentUser?.role === 'admin';

  const [customers, setCustomers]     = useState([]);
  const [loading, setLoading]         = useState(false);
  const [search, setSearch]           = useState('');
  const [filterType, setFilterType]   = useState('');
  const [page, setPage]               = useState(1);
  const [pagination, setPagination]   = useState({});

  const [selectedCustomer, setSelectedCustomer]   = useState(null);
  const [showDetail, setShowDetail]               = useState(false);
  const [detailLoading, setDetailLoading]         = useState(false);
  const [detailTab, setDetailTab]                 = useState('info');

  // VIP
  const [showVipDialog, setShowVipDialog] = useState(false);
  const [vipForm, setVipForm]             = useState({ is_vip: true, months: 1, note: '' });
  const [vipLoading, setVipLoading]       = useState(false);

  // 代理
  const [showAgentDialog, setShowAgentDialog] = useState(false);
  const [agentForm, setAgentForm]             = useState({ is_agent: true, commission_rate: 50 });
  const [agentLoading, setAgentLoading]       = useState(false);

  // 余额
  const [balanceData, setBalanceData]           = useState(null);
  const [balanceLoading, setBalanceLoading]     = useState(false);
  const [showAdjustDialog, setShowAdjustDialog] = useState(false);
  const [adjustForm, setAdjustForm]             = useState({ amount: '', type: 'recharge', notes: '' });
  const [adjustLoading, setAdjustLoading]       = useState(false);

  // 佣金
  const [commissionLogs, setCommissionLogs]       = useState([]);
  const [commissionLoading, setCommissionLoading] = useState(false);

  // VPN
  const [vpnSubs, setVpnSubs]               = useState([]);
  const [vpnPool, setVpnPool]               = useState([]);
  const [showVpnAssign, setShowVpnAssign]   = useState(false);
  const [selectedPoolId, setSelectedPoolId] = useState('');
  const [vpnLoading, setVpnLoading]         = useState(false);
  const [showVpnUrls, setShowVpnUrls]       = useState({});

  useEffect(() => { loadCustomers(); }, [page]);

  useEffect(() => {
    const timer = setTimeout(() => { setPage(1); loadCustomers(); }, 500);
    return () => clearTimeout(timer);
  }, [search, filterType]);

  const loadCustomers = async () => {
    setLoading(true);
    try {
      const params = { search, page, limit: 20 };
      if (filterType) params.filter_type = filterType;
      const res = await customerApi.getList(params);
      if (res.success) { setCustomers(res.data); setPagination(res.pagination); }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const openDetail = async (customer) => {
    setSelectedCustomer(customer);
    setShowDetail(true);
    setDetailLoading(true);
    setDetailTab('info');
    setBalanceData(null);
    setCommissionLogs([]);
    setVpnSubs([]);
    try {
      const res = await customerApi.getDetail(customer.id);
      if (res.success) setSelectedCustomer(res.data);
      if (ENABLE_VPN) {
        const vpnRes = await vpnApi.getCustomerSubscriptions(customer.id);
        if (vpnRes.success) setVpnSubs(vpnRes.data);
      }
    } catch (err) { console.error(err); }
    finally { setDetailLoading(false); }
  };

  const handleTabChange = (tab) => {
    setDetailTab(tab);
    if (tab === 'balance' && !balanceData) loadBalance(selectedCustomer.id);
    if (tab === 'commission') loadCommission(selectedCustomer.id);
  };

  const loadBalance = async (id) => {
    setBalanceLoading(true);
    try {
      const res = await customerApi.getBalance(id);
      if (res.success) setBalanceData(res.data);
    } catch (err) { console.error(err); }
    finally { setBalanceLoading(false); }
  };

  const loadCommission = async (id) => {
    setCommissionLoading(true);
    try {
      const res = await customerApi.getCommissionLogs(id);
      if (res.success) setCommissionLogs(res.data);
    } catch (err) { console.error(err); }
    finally { setCommissionLoading(false); }
  };

  const openVipDialog = (customer, mode = 'open') => {
    setSelectedCustomer(customer);
    setVipForm({ is_vip: mode !== 'cancel', months: 1, note: '' });
    setShowVipDialog(true);
    setShowDetail(false);
  };

  const handleSetVip = async () => {
    setVipLoading(true);
    try {
      const res = await customerApi.setVip(selectedCustomer.id, vipForm);
      if (res.success) {
        alert(res.message);
        setShowVipDialog(false);
        loadCustomers();
      } else { alert(res.message || '操作失败'); }
    } catch (err) { alert(err.message || '操作失败'); }
    finally { setVipLoading(false); }
  };

  const openAgentDialog = (customer) => {
    setSelectedCustomer(customer);
    setAgentForm({ is_agent: !customer.is_agent, commission_rate: customer.agent_commission_rate || 50 });
    setShowAgentDialog(true);
  };

  const handleSetAgent = async () => {
    setAgentLoading(true);
    try {
      const res = await customerApi.setSuperVip(selectedCustomer.id, {
        is_agent: agentForm.is_agent,
        commission_rate: agentForm.commission_rate
      });
      if (res.success) {
        alert(res.message);
        setShowAgentDialog(false);
        loadCustomers();
        const detail = await customerApi.getDetail(selectedCustomer.id);
        if (detail.success) setSelectedCustomer(detail.data);
      } else { alert(res.message || '操作失败'); }
    } catch (err) { alert(err.message || '操作失败'); }
    finally { setAgentLoading(false); }
  };

  const handleAdjustBalance = async () => {
    if (!adjustForm.amount || parseFloat(adjustForm.amount) <= 0) { alert('请输入有效金额'); return; }
    setAdjustLoading(true);
    try {
      const res = await customerApi.adjustBalance(selectedCustomer.id, adjustForm);
      if (res.success) {
        alert(res.message);
        setShowAdjustDialog(false);
        setAdjustForm({ amount: '', type: 'recharge', notes: '' });
        loadBalance(selectedCustomer.id);
      } else { alert(res.message || '操作失败'); }
    } catch (err) { alert(err.response?.data?.message || '操作失败'); }
    finally { setAdjustLoading(false); }
  };

  const openVpnAssign = async () => {
    setVpnLoading(true);
    try {
      const res = await vpnApi.getPoolList({ is_assigned: 0 });
      if (res.success) setVpnPool(res.data.filter(p => p.status === 'active'));
    } catch (err) { console.error(err); }
    finally { setVpnLoading(false); }
    setSelectedPoolId('');
    setShowVpnAssign(true);
  };

  const handleAssignVpn = async () => {
    if (!selectedPoolId) { alert('请选择一个账号'); return; }
    setVpnLoading(true);
    try {
      const res = await vpnApi.assignToCustomer({ pool_id: parseInt(selectedPoolId), customer_id: selectedCustomer.id });
      if (res.success) {
        alert('VPN 账号分配成功');
        setShowVpnAssign(false);
        const vpnRes = await vpnApi.getCustomerSubscriptions(selectedCustomer.id);
        if (vpnRes.success) setVpnSubs(vpnRes.data);
      } else { alert(res.message || '分配失败'); }
    } catch (err) { alert(err.message || '分配失败'); }
    finally { setVpnLoading(false); }
  };

  const handleUnassignVpn = async (pool_id) => {
    if (!window.confirm('确定取消该 VPN 账号的分配？')) return;
    try {
      const res = await vpnApi.unassignFromCustomer(pool_id);
      if (res.success) {
        alert('已取消分配');
        const vpnRes = await vpnApi.getCustomerSubscriptions(selectedCustomer.id);
        if (vpnRes.success) setVpnSubs(vpnRes.data);
      } else { alert(res.message || '操作失败'); }
    } catch (err) { alert('操作失败'); }
  };

  const toggleVpnUrl = (id) => setShowVpnUrls(prev => ({ ...prev, [id]: !prev[id] }));

  return (
    <div className="p-6 space-y-6 overflow-y-auto h-full">

      {/* 头部 */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold">客户管理</h2>
          <p className="text-xs text-gray-600 mt-1">
            所有 H5 注册客户
            {pagination.total > 0 && <span className="ml-2 text-blue-600">共 {pagination.total} 位</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={filterType} onChange={e => setFilterType(e.target.value)}
            className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm">
            <option value="">全部客户</option>
            <option value="agent">代理客户</option>
            <option value="vip">VIP 客户</option>
            <option value="vip_expired">过期 VIP</option>
            <option value="normal">普通客户</option>
          </select>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="搜索姓名、手机号、用户名..."
              className="pl-9 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm w-64" />
          </div>
        </div>
      </div>

      {/* 客户列表 */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : customers.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-12 text-center">
          <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500">暂无客户</p>
        </div>
      ) : (
        <CustomerTable
          customers={customers}
          pagination={pagination}
          page={page}
          setPage={setPage}
          isAdmin={isAdmin}
          onOpenDetail={openDetail}
          onOpenVip={openVipDialog}
        />
      )}

      {/* 客户详情弹窗 */}
      {showDetail && selectedCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
            <div className="p-5 border-b flex justify-between items-center">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-bold">{selectedCustomer.name || selectedCustomer.username || '匿名'}</h3>
                {isVipActive(selectedCustomer) && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                    <Crown className="w-3 h-3" />VIP
                  </span>
                )}
                {selectedCustomer.is_agent === 1 && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                    <Star className="w-3 h-3" />代理
                  </span>
                )}
              </div>
              <button onClick={() => setShowDetail(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b">
              {[
                { key: 'info',       label: '基本信息' },
                { key: 'balance',    label: '余额管理' },
                ...(selectedCustomer.is_agent ? [{ key: 'commission', label: '佣金记录' }] : []),
              ].map(tab => (
                <button key={tab.key} onClick={() => handleTabChange(tab.key)}
                  className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
                    detailTab === tab.key
                      ? 'text-blue-600 border-b-2 border-blue-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}>
                  {tab.label}
                </button>
              ))}
            </div>

            {detailLoading ? (
              <div className="flex-1 flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto p-5">
                {detailTab === 'info' && (
                  <InfoTab
                    customer={selectedCustomer}
                    isAdmin={isAdmin}
                    onOpenVip={(mode) => openVipDialog(selectedCustomer, mode)}
                    onOpenAgent={() => openAgentDialog(selectedCustomer)}
                    onOpenVpnAssign={openVpnAssign}
                    onUnassignVpn={handleUnassignVpn}
                    vpnSubs={vpnSubs}
                    showVpnUrls={showVpnUrls}
                    toggleVpnUrl={toggleVpnUrl}
                  />
                )}
                {detailTab === 'balance' && (
                  <BalanceTab
                    balanceData={balanceData}
                    loading={balanceLoading}
                    isAdmin={isAdmin}
                    onOpenAdjust={() => setShowAdjustDialog(true)}
                  />
                )}
                {detailTab === 'commission' && (
                  <CommissionTab
                    customer={selectedCustomer}
                    logs={commissionLogs}
                    loading={commissionLoading}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* 弹窗 */}
      {showVipDialog && (
        <VipDialog
          customer={selectedCustomer}
          form={vipForm}
          setForm={setVipForm}
          onConfirm={handleSetVip}
          onClose={() => setShowVipDialog(false)}
          loading={vipLoading}
        />
      )}

      {showAgentDialog && (
        <AgentDialog
          customer={selectedCustomer}
          form={agentForm}
          setForm={setAgentForm}
          onConfirm={handleSetAgent}
          onClose={() => setShowAgentDialog(false)}
          loading={agentLoading}
        />
      )}

      {showAdjustDialog && (
        <BalanceAdjustDialog
          customer={selectedCustomer}
          balance={balanceData?.balance}
          form={adjustForm}
          setForm={setAdjustForm}
          onConfirm={handleAdjustBalance}
          onClose={() => setShowAdjustDialog(false)}
          loading={adjustLoading}
        />
      )}

      {showVpnAssign && (
        <VpnAssignDialog
          customer={selectedCustomer}
          pool={vpnPool}
          selectedPoolId={selectedPoolId}
          setSelectedPoolId={setSelectedPoolId}
          onConfirm={handleAssignVpn}
          onClose={() => setShowVpnAssign(false)}
          loading={vpnLoading}
        />
      )}
    </div>
  );
};

export default CustomersPage;